import { initContract } from "@ts-rest/core";
import { z } from "zod";
/**
 * The project-management board at `/pm` (ADR-055, on every realm) — the wire shapes shared by the API, the web board and Claude's
 * MCP tools. Each route lands in `pmBoardContract` together with its handler (phase 2: read + admin; phases 3–4: task
 * commands, media, Drehbuch import), so the contract never advertises a route or a status code that no handler
 * implements.
 *
 * Board-specific enums carry stable KEYS on the wire (`afz`, `geklaert`, `prototyp-2`); the UI translates them
 * (ADR-048) — a display name never travels as an identifier.
 */
/** The MS lifecycle (D4). `kein_drehbuch` until the first import moves it to `konzept`. */
export const PmPhaseSchema = z.enum(["kein_drehbuch", "konzept", "prototyp", "finalisiert"]);
/**
 * The key every task, step annotation and activity row is stamped with (D4): `konzept` | `prototyp-<n>` |
 * `finalisiert`. The board shows only rows whose key equals the MS's current one.
 */
export const PmPhaseKeySchema = z.string().regex(/^(konzept|prototyp-[1-9][0-9]*|finalisiert)$/);
/**
 * The phase key of an MS, or `null` while it has no Drehbuch (nothing can be phase-stamped yet). Throws on an
 * inconsistent pair — the DB CHECK makes that impossible for a stored row, so reaching it is a bug, not user input.
 */
export function pmPhaseKeyOf(phase, prototypeNumber) {
    if (phase === "kein_drehbuch")
        return null;
    if (phase === "prototyp") {
        if (prototypeNumber === null || !Number.isInteger(prototypeNumber) || prototypeNumber < 1) {
            throw new Error(`pmPhaseKeyOf: phase 'prototyp' needs a prototype number ≥ 1, got ${String(prototypeNumber)}`);
        }
        return `prototyp-${prototypeNumber}`;
    }
    if (prototypeNumber !== null) {
        throw new Error(`pmPhaseKeyOf: only phase 'prototyp' carries a prototype number, got ${phase} + ${prototypeNumber}`);
    }
    return phase;
}
/** An institute's key (D2): one per user, assigned by a PM admin. A realm's institutes are its `pmBoard.institutes`. */
export const PmInstituteSchema = z.string().regex(/^[a-z0-9-]{1,40}$/);
/** `initial` = where new tasks land, `done` = "Geklärt" (readiness + the progress chip). Exactly one each per realm. */
export const PmColumnRoleSchema = z.enum(["initial", "done"]);
export const PmActivityObjectTypeSchema = z.enum([
    "scenario",
    "micro_scenario",
    "step_annotation",
    "variant_annotation",
    "task",
    "task_media",
    "task_annotation",
    "drehbuch",
    "column",
    "member",
    "tag",
]);
export const PmActivityActionSchema = z.enum([
    "created",
    "edited",
    "moved",
    "deleted",
    "flagged",
    "unflagged",
    "imported",
    "phase_changed",
    "training_url_changed",
    "assigned",
    "tags_changed",
]);
/** More than any realm has tags: only a bound on the request. */
const MAX_TAGS_PER_TASK = 50;
const distinct = (list) => new Set(list).size === list.length;
/** The tag colours: a palette name, one `--tag-*` token each in both themes, never a hex value. */
export const PM_TAG_COLORS = ["blue", "green", "orange", "purple", "teal", "red", "yellow", "gray"];
export const PmTagColorSchema = z.enum(PM_TAG_COLORS);
/** 500 MB per media file (ADR-055 §5.2) — the upload streams and stops here; the DB CHECK is the backstop. */
export const PM_MEDIA_MAX_BYTES = 500 * 1024 * 1024;
/**
 * What a task accepts (ADR-055 D7: images, video, PDF, Office documents; plus ZIP, CSV and TXT — owner, 2026-10-29) —
 * extension → the type the object is stored and served as. An explicit allow-list: nothing a browser executes (`.svg`,
 * `.html`). The API additionally checks the file's first bytes against the extension, so a renamed file is refused; CSV
 * and TXT have no signature, so for them the check is "this is text" (no control or NUL bytes). One source of truth for
 * the API and the web picker's `accept`.
 */
export const PM_MEDIA_TYPES = Object.freeze(Object.assign(Object.create(null), {
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".gif": "image/gif",
    ".webp": "image/webp",
    ".mp4": "video/mp4",
    ".m4v": "video/mp4",
    ".mov": "video/quicktime",
    ".webm": "video/webm",
    ".pdf": "application/pdf",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ".doc": "application/msword",
    ".xls": "application/vnd.ms-excel",
    ".ppt": "application/vnd.ms-powerpoint",
    ".zip": "application/zip",
    ".csv": "text/csv",
    ".txt": "text/plain",
}));
/** The accepted extensions, lower-case with the dot (`.jpg`) — the error message and the picker's `accept`. */
export const PM_MEDIA_EXTENSIONS = Object.freeze(Object.keys(PM_MEDIA_TYPES));
/** The stored type for a file name, or `null` when the extension is not accepted (null-prototype lookup). */
export function pmMediaTypeFor(fileName) {
    const dot = fileName.lastIndexOf(".");
    if (dot < 0)
        return null;
    return PM_MEDIA_TYPES[fileName.slice(dot).toLowerCase()] ?? null;
}
const Id = z.number().int().positive();
const IsoDate = z.string(); // ISO-8601 UTC
/** A stable Drehbuch key: `S03`, `MS03` — letters, digits, `_`, `-`. */
const StableKey = z.string().min(1).max(20).regex(/^[A-Za-z0-9_-]+$/);
/** Who did something — the author label "Name · Institut" ("ohne Institut" when `institute` is null). */
export const PmPersonSchema = z.object({ userId: Id, name: z.string(), institute: PmInstituteSchema.nullable() });
export const PmScenarioSchema = z.object({ id: Id, title: z.string(), sortOrder: z.number().int() });
export const PmMicroScenarioSchema = z.object({
    id: Id,
    scenarioId: Id,
    code: z.string(),
    title: z.string(),
    sortOrder: z.number().int(),
    phase: PmPhaseSchema,
    prototypeNumber: z.number().int().positive().nullable(),
    /** `pmPhaseKeyOf(phase, prototypeNumber)` — sent so no client re-derives it. */
    phaseKey: PmPhaseKeySchema.nullable(),
    drehbuchVersion: z.number().int().positive().nullable(),
    trainingUrl: z.string().url().nullable(),
});
export const PmVariantSchema = z.object({ id: Id, stableKey: StableKey, title: z.string(), text: z.string(), sortOrder: z.number().int() });
export const PmStepSchema = z.object({
    id: Id,
    stableKey: StableKey,
    number: z.number().int(),
    title: z.string(),
    points: z.array(z.string()),
});
export const PmStepAnnotationSchema = z.object({
    id: Id,
    stepId: Id,
    phaseKey: PmPhaseKeySchema,
    text: z.string(),
    author: PmPersonSchema,
    createdAt: IsoDate,
    editedAt: IsoDate.nullable(),
});
export const PmVariantAnnotationSchema = z.object({
    id: Id,
    variantId: Id,
    phaseKey: PmPhaseKeySchema,
    text: z.string(),
    author: PmPersonSchema,
    createdAt: IsoDate,
    editedAt: IsoDate.nullable(),
});
export const PmColumnSchema = z.object({
    id: Id,
    key: z.string(),
    title: z.string(),
    sortOrder: z.number().int(),
    role: PmColumnRoleSchema.nullable(),
    /** Its tasks stay on the board through a phase change ("Für Später", the D4 exception). */
    persistent: z.boolean(),
});
/** A tag's key: stable, unique per realm. Imports and skills address a tag by it. */
export const PmTagKeySchema = z.string().regex(/^[a-z0-9-]{1,40}$/);
/** Who is responsible for a task — not whose turn it is. Realm-wide like a column, created through the admin routes only. */
export const PmTagSchema = z.object({
    id: Id,
    key: PmTagKeySchema,
    label: z.string(),
    color: PmTagColorSchema,
    sortOrder: z.number().int(),
});
export const PmTaskMediaSchema = z.object({
    id: Id,
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    uploadedBy: PmPersonSchema,
    createdAt: IsoDate,
});
export const PmTaskAnnotationSchema = z.object({
    id: Id,
    taskId: Id,
    text: z.string(),
    author: PmPersonSchema,
    createdAt: IsoDate,
    editedAt: IsoDate.nullable(),
});
export const PmTaskSchema = z.object({
    id: Id,
    msId: Id,
    phaseKey: PmPhaseKeySchema,
    /** `null` = "Ganzes MS". */
    stepId: Id.nullable(),
    columnId: Id,
    title: z.string(),
    description: z.string(),
    flaggedForMeeting: z.boolean(),
    createdBy: PmPersonSchema,
    createdAt: IsoDate,
    updatedAt: IsoDate,
    /** Optimistic lock (D9): edit/move send it back; a mismatch is a 409 "Wurde inzwischen geändert". */
    version: z.number().int().positive(),
    media: z.array(PmTaskMediaSchema),
    annotationCount: z.number().int().nonnegative(),
    /** The task's tags, in the tags' order; the labels are in the view's `tags`. */
    tagIds: z.array(Id),
});
export const PmActivitySchema = z.object({
    id: z.string(), // bigint on the DB side — a string on the wire, never a lossy JS number
    msId: Id.nullable(),
    actor: PmPersonSchema,
    objectType: PmActivityObjectTypeSchema,
    objectId: Id,
    action: PmActivityActionSchema,
    phaseKey: z.string().nullable(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    createdAt: IsoDate,
});
export const PmMemberSchema = z.object({ userId: Id, name: z.string(), institute: PmInstituteSchema });
/** A variant's key: the explicit one, else its position (`V1` for the first). */
export const pmVariantKey = (v, index) => v.stableKey ?? `V${index + 1}`;
/**
 * The Drehbuch import (D6) — the seam between Claude and the board, sent as `PUT /pm/ms/:id/drehbuch`. Claude parses
 * `unity-projects/.claude/references/afzrostock/drehbuecher/MSxx.md` into this. Idempotent per `version`: the same
 * version with the same content is a no-op, the same version with different content a 409 — a changed `.md` always
 * needs a new version number. Steps and variants are matched on `stableKey`; a variant without one is keyed by its
 * position (`V1`, `V2`, …), so give explicit keys as soon as variants can be inserted or reordered.
 */
export const PmDrehbuchImportSchema = z
    .object({
    version: z.number().int().positive(),
    sourceMd: z.string().min(1).max(2_000_000),
    /** "Überblick": what the MS is about, shown at the top of the Drehbuch. Lives in the version's `content`. */
    overview: z.string().trim().max(20_000).optional(),
    variants: z
        .array(z.object({ stableKey: StableKey.optional(), title: z.string().trim().min(1).max(500), text: z.string().max(20_000) }))
        .max(50),
    steps: z
        .array(z.object({
        stableKey: StableKey,
        number: z.number().int().nonnegative(),
        title: z.string().trim().min(1).max(500),
        points: z.array(z.string().max(5_000)).max(200),
    }))
        .max(500),
    /**
     * Applied on the FIRST import only (the skill's "benötigte Inhalte" and "Fragen an den Kunden"): created in the
     * column with key `afz` (fallback: the initial column), on the named step or on "Ganzes MS" without `stableKey`.
     */
    readinessTasks: z
        .array(z.object({
        title: z.string().trim().min(1).max(500),
        description: z.string().max(20_000).default(""),
        stableKey: StableKey.optional(),
        /** Tag keys of this realm, used by the first import only: that is the only one that creates these tasks. */
        tags: z.array(PmTagKeySchema).max(MAX_TAGS_PER_TASK).refine(distinct, "duplicate tag").optional(),
    }))
        .max(200)
        .optional(),
})
    .superRefine((body, ctx) => {
    const seen = new Set();
    body.steps.forEach((s, i) => {
        if (seen.has(s.stableKey)) {
            ctx.addIssue({ code: "custom", path: ["steps", i, "stableKey"], message: `duplicate stableKey ${s.stableKey}` });
        }
        seen.add(s.stableKey);
    });
    const seenVariants = new Set();
    body.variants.forEach((v, i) => {
        const key = pmVariantKey(v, i);
        if (seenVariants.has(key))
            ctx.addIssue({ code: "custom", path: ["variants", i, "stableKey"], message: `duplicate variant key ${key}` });
        seenVariants.add(key);
    });
    body.readinessTasks?.forEach((t, i) => {
        if (t.stableKey !== undefined && !seen.has(t.stableKey)) {
            ctx.addIssue({ code: "custom", path: ["readinessTasks", i, "stableKey"], message: `unknown stableKey ${t.stableKey}` });
        }
    });
});
export const PmDrehbuchImportResultSchema = z.object({
    /** `unchanged` = the same version with the same content was already imported (a no-op). */
    outcome: z.enum(["imported", "unchanged"]),
    version: z.number().int().positive(),
    /** Tasks whose step was removed by this import and now sit on "Ganzes MS" — Claude reports them. */
    movedTasks: z.array(z.object({ taskId: Id, title: z.string(), formerStableKey: StableKey })),
    /** Readiness tasks created (first import only). */
    createdTaskIds: z.array(Id),
    /** The MS's phase after the import — the first import moves `kein_drehbuch` → `konzept`. */
    phase: PmPhaseSchema,
});
// ── Phase 2: read + admin routes ────────────────────────────────────────────────────────────────────────────────────
const c = initContract();
/**
 * Every PM route answers 404 `PM_NOT_FOUND` to a caller without access to the realm's board — the same answer as an
 * unknown id, so they cannot even tell the routes exist (ADR-055 D1, the server-side gate). Access: a SuperAdmin, an
 * assigned member, or anyone signed in where the realm has `pmBoard.openToAllUsers`.
 */
const PmNotFoundSchema = z.object({ message: z.string(), code: z.literal("PM_NOT_FOUND") });
/** A PM-admin-only route (D2: PM admin = the realm's SuperAdmin) called by a PM user. */
const PmForbiddenSchema = z.object({ message: z.string(), code: z.literal("PM_FORBIDDEN") });
const PmConflictSchema = z.object({
    message: z.string(),
    code: z.enum(["PM_CODE_TAKEN", "PM_COLUMN_IN_USE", "PM_COLUMN_PROTECTED", "PM_TAG_IN_USE", "PM_TAG_TAKEN"]),
});
const PmBadRequestSchema = z.object({ message: z.string() });
const UnauthorizedSchema = z.object({ message: z.string() });
const IdParam = z.coerce.number().int().positive().max(2147483647);
const Title = z.string().trim().min(1).max(500);
const SortOrder = z.number().int().min(0).max(100_000);
const MsCode = z.string().trim().min(1).max(20).regex(/^[A-Za-z0-9_-]+$/);
export const PmProgressSchema = z.object({
    /** Current-phase tasks in the `done` column. */
    done: z.number().int().nonnegative(),
    /** All current-phase tasks. */
    total: z.number().int().nonnegative(),
});
export const PmOverviewSchema = z.object({
    /** The caller is a PM admin (SuperAdmin). A UI signal only — every admin route re-checks. */
    isAdmin: z.boolean(),
    scenarios: z.array(PmScenarioSchema.extend({
        microScenarios: z.array(PmMicroScenarioSchema.extend({ progress: PmProgressSchema })),
    })),
});
/** One MS as the board shows it: the read-only Drehbuch plus the tasks and annotations of ONE phase (D4). */
export const PmMicroScenarioViewSchema = z.object({
    isAdmin: z.boolean(),
    ms: PmMicroScenarioSchema,
    /** The phase this view shows — the MS's current one unless an admin asked for an earlier one. NULL = no Drehbuch yet. */
    viewedPhaseKey: PmPhaseKeySchema.nullable(),
    /** "Überblick" of the current Drehbuch version; empty when it has none. */
    overview: z.string(),
    variants: z.array(PmVariantSchema),
    steps: z.array(PmStepSchema),
    stepAnnotations: z.array(PmStepAnnotationSchema),
    variantAnnotations: z.array(PmVariantAnnotationSchema),
    columns: z.array(PmColumnSchema),
    tags: z.array(PmTagSchema),
    tasks: z.array(PmTaskSchema),
    /** The caller's previous visit of this MS ("seit dem letzten Besuch"); NULL on the first. */
    lastVisitAt: z.string().nullable(),
});
/** A realm user as the admin's institute assignment lists them. */
export const PmMemberListItemSchema = z.object({ userId: Id, name: z.string(), institute: PmInstituteSchema.nullable() });
/** Earlier phases are readable by a PM admin only (D4); a PM user always gets the current one. */
const PhaseQuery = z.object({ phase: PmPhaseKeySchema.optional() });
export const pmBoardContract = c.router({
    getPmOverview: {
        method: "GET",
        path: "/pm",
        responses: { 200: PmOverviewSchema, 401: UnauthorizedSchema, 404: PmNotFoundSchema },
        summary: "The landing page: every scenario with its micro-scenarios and their current-phase progress (ADR-055).",
    },
    getPmMicroScenario: {
        method: "GET",
        path: "/pm/ms/:id",
        pathParams: z.object({ id: IdParam }),
        query: PhaseQuery,
        responses: { 200: PmMicroScenarioViewSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "One MS: Drehbuch, columns, and the tasks + step annotations of its current phase (an earlier one: admin only).",
    },
    getPmActivity: {
        method: "GET",
        path: "/pm/ms/:id/activity",
        pathParams: z.object({ id: IdParam }),
        // Wider than the MS view's PhaseQuery (diff panel): the log also holds `kein_drehbuch` rows (the MS's creation and
        // every edit before its first import), which must stay readable after the MS leaves that phase (D4). Tasks and
        // annotations never carry that key, so the view keeps the narrow schema.
        query: z.object({
            phase: z.union([PmPhaseKeySchema, z.literal("kein_drehbuch")]).optional(),
            since: z.string().datetime({ offset: true }).optional(),
        }),
        responses: {
            200: z.object({ items: z.array(PmActivitySchema) }),
            400: PmBadRequestSchema,
            401: UnauthorizedSchema,
            403: PmForbiddenSchema,
            404: PmNotFoundSchema,
        },
        summary: "The MS change log (Verlauf) of one phase, newest first, at most 500 rows, optionally since a timestamp.",
    },
    listPmMembers: {
        method: "GET",
        path: "/pm/members",
        responses: { 200: z.object({ members: z.array(PmMemberListItemSchema) }), 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Every user of the realm with their institute (PM admin only).",
    },
    createPmScenario: {
        method: "POST",
        path: "/pm/scenarios",
        body: z.object({ title: Title }),
        responses: { 201: PmScenarioSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Create a scenario, appended at the end (PM admin only).",
    },
    updatePmScenario: {
        method: "PATCH",
        path: "/pm/scenarios/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ title: Title.optional(), sortOrder: SortOrder.optional() }),
        responses: { 200: PmScenarioSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Rename or reorder a scenario (PM admin only).",
    },
    createPmMicroScenario: {
        method: "POST",
        path: "/pm/ms",
        body: z.object({ scenarioId: Id, code: MsCode, title: Title }),
        responses: {
            201: PmMicroScenarioSchema,
            400: PmBadRequestSchema,
            401: UnauthorizedSchema,
            403: PmForbiddenSchema,
            404: PmNotFoundSchema,
            409: PmConflictSchema,
        },
        summary: "Create a micro-scenario in phase kein_drehbuch, appended to its scenario (PM admin only; code unique per realm).",
    },
    updatePmMicroScenario: {
        method: "PATCH",
        path: "/pm/ms/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ scenarioId: Id.optional(), code: MsCode.optional(), title: Title.optional(), sortOrder: SortOrder.optional() }),
        responses: {
            200: PmMicroScenarioSchema,
            400: PmBadRequestSchema,
            401: UnauthorizedSchema,
            403: PmForbiddenSchema,
            404: PmNotFoundSchema,
            409: PmConflictSchema,
        },
        summary: "Rename, re-code, reorder or move a micro-scenario to another scenario (PM admin only).",
    },
    setPmPhase: {
        method: "PUT",
        path: "/pm/ms/:id/phase",
        pathParams: z.object({ id: IdParam }),
        body: z
            .object({ phase: PmPhaseSchema, prototypeNumber: z.number().int().min(1).max(999).nullable() })
            .refine((b) => (b.phase === "prototyp") === (b.prototypeNumber !== null), {
            message: "prototypeNumber is required for phase 'prototyp' and must be null otherwise",
            path: ["prototypeNumber"],
        }),
        responses: { 200: PmMicroScenarioSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Set an MS phase: any target, also backwards; nothing is deleted, other phases are only hidden (D4; PM admin only).",
    },
    setPmTrainingUrl: {
        method: "PUT",
        path: "/pm/ms/:id/training-url",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ trainingUrl: z.string().url().max(2000).startsWith("https://").nullable() }),
        responses: { 200: PmMicroScenarioSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Set or clear the Ansehen link (https only, D10; PM admin only).",
    },
    createPmColumn: {
        method: "POST",
        path: "/pm/columns",
        body: z.object({ title: Title }),
        responses: { 201: PmColumnSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Add a Kanban column just before the done column (PM admin only; columns are realm-wide).",
    },
    updatePmColumn: {
        method: "PATCH",
        path: "/pm/columns/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ title: Title.optional(), sortOrder: SortOrder.optional() }),
        responses: { 200: PmColumnSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Rename or reorder a column, the start and done columns included (PM admin only).",
    },
    deletePmColumn: {
        method: "DELETE",
        path: "/pm/columns/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({}).optional(),
        responses: {
            200: z.object({ deleted: z.literal(true), movedTasks: z.number().int().nonnegative() }),
            401: UnauthorizedSchema,
            403: PmForbiddenSchema,
            404: PmNotFoundSchema,
            409: PmConflictSchema,
        },
        summary: "Delete a column; its tasks of ANY phase move to the end of the start column and keep their phase. 409 PM_COLUMN_PROTECTED for the start, done and persistent columns, 409 PM_COLUMN_IN_USE when a task moved in meanwhile: try again (PM admin only).",
    },
    createPmTag: {
        method: "POST",
        path: "/pm/tags",
        body: z.object({ key: PmTagKeySchema, label: Title, color: PmTagColorSchema }),
        responses: { 201: PmTagSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema, 409: PmConflictSchema },
        summary: "Add a tag at the end of the realm's list. 409 PM_TAG_TAKEN for a key or label it already has (PM admin only).",
    },
    updatePmTag: {
        method: "PATCH",
        path: "/pm/tags/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ label: Title.optional(), color: PmTagColorSchema.optional(), sortOrder: SortOrder.optional() }),
        responses: { 200: PmTagSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema, 409: PmConflictSchema },
        summary: "Rename, recolour or reorder a tag; its key never changes. 409 PM_TAG_TAKEN for a label it already has (PM admin only).",
    },
    deletePmTag: {
        method: "DELETE",
        path: "/pm/tags/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema, 409: PmConflictSchema },
        summary: "Delete a tag. 409 PM_TAG_IN_USE while a task on a live board carries it (current phase or a persistent column). Archived tasks lose it, and the log names them (PM admin only).",
    },
    setPmMemberInstitute: {
        method: "PUT",
        path: "/pm/members/:userId",
        pathParams: z.object({ userId: IdParam }),
        body: z.object({ institute: PmInstituteSchema.nullable() }),
        responses: { 200: PmMemberListItemSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 403: PmForbiddenSchema, 404: PmNotFoundSchema },
        summary: "Assign a user's institute (a key of the realm's config, else 400), or clear it with null (PM admin only).",
    },
});
// ── Phase 3a: task commands, meeting flag, annotations ─────────────────────────────────────────────────────────────
/**
 * Task + annotation writes act on the MS's CURRENT phase only: an earlier phase is an archive (D4), so a write to one
 * of its rows is a 404 like a row that does not exist. An MS without a Drehbuch has no phase key yet, so nothing can be
 * created on it (409 `PM_NO_PHASE`).
 */
const PmWriteConflictSchema = z.object({
    message: z.string(),
    code: z.enum(["PM_STALE", "PM_NO_PHASE"]),
});
const Version = z.number().int().positive();
const LongText = z.string().max(20_000);
const AnnotationText = z.string().trim().min(1).max(10_000);
/** The error answers every task / annotation write can give. */
const TagIds = z.array(Id).max(MAX_TAGS_PER_TASK).refine(distinct, "duplicate tag");
const WRITE_ERRORS = {
    400: PmBadRequestSchema,
    401: UnauthorizedSchema,
    403: PmForbiddenSchema,
    404: PmNotFoundSchema,
    409: PmWriteConflictSchema,
};
export const pmTaskContract = c.router({
    createPmTask: {
        method: "POST",
        path: "/pm/ms/:id/tasks",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ title: Title, description: LongText.default(""), stepId: Id.nullable(), tagIds: TagIds.default([]) }),
        responses: { 201: PmTaskSchema, ...WRITE_ERRORS },
        summary: "Create a task in the MS's current phase, in the start column, on a step of this MS or on the whole MS (null).",
    },
    updatePmTask: {
        method: "PATCH",
        path: "/pm/tasks/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ version: Version, title: Title.optional(), description: LongText.optional(), stepId: Id.nullable().optional() }),
        responses: { 200: PmTaskSchema, ...WRITE_ERRORS },
        summary: "Edit a task's title, description or step. 409 PM_STALE when `version` is not the current one (D9).",
    },
    movePmTask: {
        method: "PUT",
        path: "/pm/tasks/:id/column",
        pathParams: z.object({ id: IdParam }),
        /** `beforeTaskId`: insert before this task of the target column; null = at the end; left out = at the end of ANOTHER column, nothing within the same one. */
        body: z.object({ version: Version, columnId: Id, beforeTaskId: Id.nullable().optional() }),
        responses: { 200: PmTaskSchema, ...WRITE_ERRORS },
        summary: "Move a task to a column and position (drag & drop or the keyboard menu). 409 PM_STALE on a stale version or when beforeTaskId is no longer in that column.",
    },
    setPmTaskMeeting: {
        method: "PUT",
        path: "/pm/tasks/:id/meeting",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ flagged: z.boolean() }),
        responses: { 200: PmTaskSchema, ...WRITE_ERRORS },
        summary: "Set or clear 'In Meeting besprechen'. Idempotent, so no version is needed.",
    },
    setPmTaskTags: {
        method: "PUT",
        path: "/pm/tasks/:id/tags",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ version: Version, tagIds: TagIds }),
        responses: { 200: PmTaskSchema, ...WRITE_ERRORS },
        summary: "Set a task's whole tag list (any PM user). 400 for a tag this realm does not have, 409 PM_STALE when `version` is not the current one (D9).",
    },
    deletePmTask: {
        method: "DELETE",
        path: "/pm/tasks/:id",
        pathParams: z.object({ id: IdParam }),
        query: z.object({ version: z.coerce.number().int().positive() }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), ...WRITE_ERRORS },
        summary: "Hard-delete a task with its annotations and media rows; the log keeps a full snapshot (D5). 409 PM_STALE on a stale version.",
    },
    listPmTaskAnnotations: {
        method: "GET",
        path: "/pm/tasks/:id/annotations",
        pathParams: z.object({ id: IdParam }),
        responses: { 200: z.object({ items: z.array(PmTaskAnnotationSchema) }), 401: UnauthorizedSchema, 404: PmNotFoundSchema },
        summary: "A task's annotations, oldest first (the task details). Any task the caller may see: current phase, or any phase for an admin.",
    },
    getPmTaskActivity: {
        method: "GET",
        path: "/pm/tasks/:id/activity",
        pathParams: z.object({ id: IdParam }),
        responses: { 200: z.object({ items: z.array(PmActivitySchema) }), 401: UnauthorizedSchema, 404: PmNotFoundSchema },
        summary: "A task's own history (the task and its annotations and media), newest first.",
    },
    createPmTaskAnnotation: {
        method: "POST",
        path: "/pm/tasks/:id/annotations",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ text: AnnotationText }),
        responses: { 201: PmTaskAnnotationSchema, ...WRITE_ERRORS },
        summary: "Write an annotation on a task of the current phase.",
    },
    updatePmTaskAnnotation: {
        method: "PATCH",
        path: "/pm/task-annotations/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ text: AnnotationText }),
        responses: { 200: PmTaskAnnotationSchema, ...WRITE_ERRORS },
        summary: "Edit your OWN task annotation (owner decision 2026-10-28: author only); it then shows 'bearbeitet'.",
    },
    deletePmTaskAnnotation: {
        method: "DELETE",
        path: "/pm/task-annotations/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), ...WRITE_ERRORS },
        summary: "Delete a task annotation: the author, or a PM admin for anyone's (owner decision 2026-10-28).",
    },
    createPmStepAnnotation: {
        method: "POST",
        path: "/pm/steps/:id/annotations",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ text: AnnotationText }),
        responses: { 201: PmStepAnnotationSchema, ...WRITE_ERRORS },
        summary: "Write an annotation under a Drehbuch step, stamped with the MS's current phase. 409 PM_NO_PHASE without one.",
    },
    updatePmStepAnnotation: {
        method: "PATCH",
        path: "/pm/step-annotations/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ text: AnnotationText }),
        responses: { 200: PmStepAnnotationSchema, ...WRITE_ERRORS },
        summary: "Edit your OWN step annotation of the current phase; it then shows 'bearbeitet'.",
    },
    deletePmStepAnnotation: {
        method: "DELETE",
        path: "/pm/step-annotations/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), ...WRITE_ERRORS },
        summary: "Delete a step annotation of the current phase: the author, or a PM admin for anyone's.",
    },
    createPmVariantAnnotation: {
        method: "POST",
        path: "/pm/variants/:id/annotations",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ text: AnnotationText }),
        responses: { 201: PmVariantAnnotationSchema, ...WRITE_ERRORS },
        summary: "Write an annotation on a Drehbuch variant, stamped with the MS's current phase. 409 PM_NO_PHASE without one.",
    },
    updatePmVariantAnnotation: {
        method: "PATCH",
        path: "/pm/variant-annotations/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({ text: AnnotationText }),
        responses: { 200: PmVariantAnnotationSchema, ...WRITE_ERRORS },
        summary: "Edit your OWN variant annotation of the current phase; it then shows 'bearbeitet'.",
    },
    deletePmVariantAnnotation: {
        method: "DELETE",
        path: "/pm/variant-annotations/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), ...WRITE_ERRORS },
        summary: "Delete a variant annotation of the current phase: the author, or a PM admin for anyone's.",
    },
});
// ── Phase 3b: task media ────────────────────────────────────────────────────────────────────────────────────────────
/**
 * The UPLOAD is not a ts-rest route: it is a raw `multipart/form-data` POST (ts-rest cannot model a streamed body),
 * exactly like the asset library's upload. `POST /pm/tasks/:id/media`, one file part, answers 201 with a
 * {@link PmTaskMediaSchema}. Its errors: 400 (not multipart / no file / more than one), 404 (task unknown or not in the
 * current phase), 413 `PM_MEDIA_TOO_LARGE` (over {@link PM_MEDIA_MAX_BYTES}), 415 `PM_MEDIA_TYPE` (extension not in
 * {@link PM_MEDIA_TYPES}, or the content does not match it), 503 `PM_MEDIA_NO_STORAGE`.
 */
export const PM_TASK_MEDIA_UPLOAD_PATH = "/pm/tasks/:id/media";
const PmUnavailableSchema = z.object({ message: z.string(), code: z.literal("PM_MEDIA_NO_STORAGE") });
/** A short-lived presigned GET for one media object (D7). The browser fetches it directly; it expires on its own. */
export const PmTaskMediaUrlSchema = z.object({ url: z.string().url(), expiresInSeconds: z.number().int().positive() });
export const pmTaskMediaContract = c.router({
    getPmTaskMediaUrl: {
        method: "GET",
        path: "/pm/task-media/:id/url",
        pathParams: z.object({ id: IdParam }),
        query: z.object({ download: z.enum(["0", "1"]).optional() }),
        responses: { 200: PmTaskMediaUrlSchema, 401: UnauthorizedSchema, 404: PmNotFoundSchema, 503: PmUnavailableSchema },
        summary: "A presigned link to view (images, video, PDF open inline) or download (`download=1`, and always for Office files) one media file. Same visibility as its task.",
    },
    deletePmTaskMedia: {
        method: "DELETE",
        path: "/pm/task-media/:id",
        pathParams: z.object({ id: IdParam }),
        body: z.object({}).optional(),
        responses: { 200: z.object({ deleted: z.literal(true) }), ...WRITE_ERRORS },
        summary: "Remove a media file from a task of the current phase (any PM user); logged, the object is deleted after commit.",
    },
});
// ── Phase 4: the Drehbuch import ────────────────────────────────────────────────────────────────────────────────────
/** 409 on an import: the version exists with OTHER content, or it is older than the current one (D6, ⟵fold#4). */
const PmDrehbuchConflictSchema = z.object({
    message: z.string(),
    code: z.enum(["PM_DREHBUCH_VERSION_TAKEN", "PM_DREHBUCH_VERSION_OLD"]),
});
/** One step as a version holds it — the diff's `before` / `after`. */
const PmDrehbuchStepContentSchema = z.object({ number: z.number().int(), title: z.string(), points: z.array(z.string()) });
/**
 * What changed between two imported versions of an MS's Drehbuch (D6: "the board can show the diff to the previous
 * version"). Steps are compared on `stableKey`; only steps that differ are listed. `from` is null for the first version
 * (everything is `added`). `versions` lists every import, newest first, for a version picker.
 */
export const PmDrehbuchDiffSchema = z.object({
    from: z.number().int().positive().nullable(),
    to: z.number().int().positive(),
    overviewChanged: z.boolean(),
    variantsChanged: z.boolean(),
    steps: z.array(z.object({
        stableKey: StableKey,
        change: z.enum(["added", "removed", "changed"]),
        before: PmDrehbuchStepContentSchema.nullable(),
        after: PmDrehbuchStepContentSchema.nullable(),
    })),
    versions: z.array(z.object({ version: z.number().int().positive(), importedAt: IsoDate, importedBy: PmPersonSchema })),
});
/**
 * The import's second door (owner decision 2026-10-29, ADR-055 D6): the MS is addressed by its CODE and created with
 * its scenario when it does not exist yet — how Claude brings a new micro-scenario onto the board. For an existing MS
 * the titles are ignored (renaming or moving an MS stays an admin action on the board).
 */
export const PmDrehbuchImportByCodeSchema = z.object({
    /** The scenario the MS is created under — matched case-insensitively, created when missing. New MS only. */
    scenarioTitle: Title,
    /** The MS's title. New MS only. */
    msTitle: Title,
    drehbuch: PmDrehbuchImportSchema,
});
export const PmDrehbuchImportByCodeResultSchema = PmDrehbuchImportResultSchema.extend({
    msId: Id,
    msCreated: z.boolean(),
    scenarioCreated: z.boolean(),
});
export const pmDrehbuchContract = c.router({
    importPmDrehbuchByCode: {
        method: "PUT",
        path: "/pm/drehbuch/:code",
        pathParams: z.object({ code: MsCode }),
        body: PmDrehbuchImportByCodeSchema,
        responses: {
            200: PmDrehbuchImportByCodeResultSchema,
            400: PmBadRequestSchema,
            401: UnauthorizedSchema,
            403: PmForbiddenSchema,
            404: PmNotFoundSchema,
            409: PmDrehbuchConflictSchema,
        },
        summary: "Import a Drehbuch into the MS with this code — creating the MS (and its scenario, by title) when it does not exist yet. Same rules as the by-id import. PM admin; later Claude's service client.",
    },
    importPmDrehbuch: {
        method: "PUT",
        path: "/pm/ms/:id/drehbuch",
        pathParams: z.object({ id: IdParam }),
        body: PmDrehbuchImportSchema,
        responses: {
            200: PmDrehbuchImportResultSchema,
            400: PmBadRequestSchema,
            401: UnauthorizedSchema,
            403: PmForbiddenSchema,
            404: PmNotFoundSchema,
            409: PmDrehbuchConflictSchema,
        },
        summary: "Import a Drehbuch version (PM admin; later Claude's service client). Idempotent per version: same content → 'unchanged', other content → 409. Steps matched on stableKey; a removed step's tasks move to 'Ganzes MS'. The first import moves kein_drehbuch → konzept and creates the readiness tasks.",
    },
    getPmDrehbuchDiff: {
        method: "GET",
        path: "/pm/ms/:id/drehbuch/diff",
        pathParams: z.object({ id: IdParam }),
        query: z.object({ from: z.coerce.number().int().positive().optional(), to: z.coerce.number().int().positive().optional() }),
        responses: { 200: PmDrehbuchDiffSchema, 400: PmBadRequestSchema, 401: UnauthorizedSchema, 404: PmNotFoundSchema },
        summary: "What changed between two Drehbuch versions (default: the current one against its predecessor). Any PM user.",
    },
});
// ── Phase 6b: "seit dem letzten Besuch" ─────────────────────────────────────────────────────────────────────────────
export const PmVisitSchema = z.object({
    /** When this visit was recorded (now). */
    seenAt: IsoDate,
    /** The visit before it — what "seit dem letzten Besuch" compares against; NULL on the first. */
    previousVisitAt: IsoDate.nullable(),
});
export const pmVisitContract = c.router({
    recordPmVisit: {
        method: "PUT",
        path: "/pm/ms/:id/visit",
        pathParams: z.object({ id: IdParam }),
        body: z.object({}).optional(),
        responses: { 200: PmVisitSchema, 401: UnauthorizedSchema, 404: PmNotFoundSchema },
        summary: "Record that the caller has looked at this MS now (any PM user, own row only). Not a change to the board, so not logged.",
    },
});
//# sourceMappingURL=pm-board.contract.js.map